import { memo, useCallback, useState } from 'react'

import { type UseInviteLinkSelection } from '@/features/analytics/hooks'
import { AnalyticsTableRowTypeChannels } from '@/features/analytics/types'
import { type InviteLinkMutate, useSavedErid } from '@/shared/api'
import { Checkbox, RubleIconInline } from '@/shared/ui'
import {
    AverageReachCell,
    BudgetCell,
    type BudgetCellProps,
    CpmCell,
    CreationDateCell,
    CreativeCell,
    DateWithTimeCell,
    ErrCell,
    LinkCell,
    MarkingCell,
    MarkingCellProps,
    NameCell,
    type NameCellProps,
    ReachCell,
    StatusCell,
    type StatusCellProps,
    SubscribersCell,
} from '../shared/cells'
import {
    type UseEditSingleField,
    useEditSingleFieldBounded,
    useMarkingFormQuery,
    useTelegramMarkingDataForShare,
} from '../shared/hooks'

import { AdvertiserAccount, Channel } from '@/true-fsd/data/advertiser-account'
import { workspaceMethods } from '@synapex/shared2/entities'
import { parseTelegramPeerReference } from '@synapex/shared2/telegram'
import { useLinkToLink } from '../shared/hooks/useLinkToLink'
import {
    TABLE_COLUMNS_LAYOUT,
    type TableReactNodeRecord,
} from './tableColumnsLayout'

const useOnChangeInviteLink = (
    mutate: InviteLinkMutate,
    inviteLinkId: number
) => {
    const onChangeStatus: StatusCellProps['onChange'] = useCallback(
        (newStatus) =>
            mutate({
                inviteLinkId,
                inviteLinkStatus: newStatus,
            }),
        [mutate, inviteLinkId]
    )

    const onChangeName: NameCellProps['onChange'] = useCallback(
        (newName) =>
            mutate({
                inviteLinkId,
                name: newName,
            }),
        [mutate, inviteLinkId]
    )

    const onChangeBudget: BudgetCellProps['onChange'] = useCallback(
        (newBudget) =>
            mutate({
                inviteLinkId,
                budget: newBudget,
            }),
        [mutate, inviteLinkId]
    )

    const onChangeAdminContactId = useCallback(
        (editingRowId: number, selectedAdminContactId: number | null) =>
            mutate({
                inviteLinkId: editingRowId,
                selectedAdminContactId,
            }),
        [mutate]
    )

    return {
        onChangeStatus,
        onChangeName,
        onChangeBudget,
        onChangeAdminContactId,
    }
}

export type TableRowProps = {
    data: AnalyticsTableRowTypeChannels
    selected: boolean
    onSelect: UseInviteLinkSelection['onLinkSelect']

    mutate: InviteLinkMutate
    groupId: number
    activeChannel: Channel
    activeAccount: AdvertiserAccount | null
} & UseEditSingleField &
    Pick<MarkingCellProps, 'markingStatus'>

export const TableRow = memo(function TableRow({
    data,
    selected,
    onSelect: onSelectProp,

    mutate,
    groupId,
    activeChannel,

    activeAccount,

    editSingleField,
    editSingleFieldDispatch,

    markingStatus,
    ...nameTdProps
}: TableRowProps) {
    const {
        link_id,
        status,
        budget,
        creationDate,
        publishedAt,
        link,
        activeCreative,
        err,
        subscribers,
        averageReach,
        CPMForcasted,
        CTR,
        CPS,
        unsubscribed,
        publishingDeletionDate,
        reach,
        CPM,
        subscribed,
        subscribedPerDay,
        unsubscriptionPercentage,
        total_CPS,
        totalSubscribed,
        publisher,
    } = data

    let editName = false
    let editBudget = false

    if (editSingleField && editSingleField.linkId === link_id) {
        editName = editSingleField.field === 'name'
        editBudget = editSingleField.field === 'budget'
    }

    const { maybeCopyLinkToLinkToClipboard, trElementId } = useLinkToLink({
        activeWorkspace: activeChannel,
        groupId,
        link_id,
    })

    const onSelect = useCallback(() => {
        onSelectProp(data)
        maybeCopyLinkToLinkToClipboard()
    }, [onSelectProp, data, maybeCopyLinkToLinkToClipboard])

    const {
        onChangeBudget,
        onChangeName,
        onChangeStatus,
        onChangeAdminContactId,
    } = useOnChangeInviteLink(mutate, link_id)
    const [budgetErrorKey, setBudgetErrorKey] = useState(0)
    const onBudgetError = useCallback(() => setBudgetErrorKey((k) => k + 1), [])

    const { onChangeEditBudget, onChangeEditName } = useEditSingleFieldBounded(
        editSingleFieldDispatch,
        link_id
    )

    const wwld = { ...workspaceMethods.getWd(activeChannel), link_id }

    const { ref, query: markingModalFormDataQuery } = useMarkingFormQuery(
        wwld,
        Boolean(
            markingStatus.status === 'available' &&
                parseTelegramPeerReference(data.name)
        )
    )

    const ordCreativeFlag = markingModalFormDataQuery.data?.creative_flag
    const allowShareCreative =
        ordCreativeFlag &&
        (markingModalFormDataQuery.data?.mark_text ||
            markingModalFormDataQuery.data?.mark_media)

    const publishOptionFlag =
        markingModalFormDataQuery.data?.publish_option_flag

    const savedEridKktu = useSavedErid({
        ...wwld,
        draftContractId: markingModalFormDataQuery.data?.draft_contract_id,
        agencyContractId: markingModalFormDataQuery.data?.agency_contract_id,
        creativeId: activeCreative?.id,
        customContractId: markingModalFormDataQuery.data?.custom_contract_id,
    })

    const telegramMarkingData = useTelegramMarkingDataForShare({
        ...wwld,
        markingFormData: markingModalFormDataQuery.data ?? null,
        savedErid: savedEridKktu.savedErid ?? null,
        adOrdContragentId:
            markingStatus.status === 'available'
                ? (markingStatus.adOrdContragentId ?? null)
                : null,
        markingStatusAvailable: markingStatus.status === 'available',
    })

    const hasMarkingFormData = Boolean(markingModalFormDataQuery.data)

    const tableRow: TableReactNodeRecord = {
        marking: (
            <MarkingCell
                {...{
                    ...wwld,
                    savedEridKktu,
                    activeAccount,
                    data,
                    markingStatus,
                    groupId,
                    markingModalFormDataQuery,
                    onChangeAdminContactId,
                    budget,
                    onBudgetError,
                }}
            />
        ),
        select: (
            <div className="flex items-center justify-center">
                <Checkbox checked={selected} onChange={onSelect} />
            </div>
        ),
        creationDate: <CreationDateCell>{creationDate}</CreationDateCell>,
        name: (
            <NameCell
                {...nameTdProps}
                onChange={onChangeName}
                data={data}
                edit={editName}
                onChangeEdit={onChangeEditName}
            />
        ),
        status: (
            <StatusCell
                value={status}
                onChange={onChangeStatus}
                className={'w-full'}
            />
        ),
        creative: (
            <CreativeCell
                {...wwld}
                withShare
                activeCreative={activeCreative}
                published={status === 'published'}
                privateChannel={
                    publisher && !publisher.telegram_channel_username
                }
                postDeleted={Boolean(publisher?.link_deleted_at)}
                groupId={groupId}
                disabled={ordCreativeFlag}
                allowShare={allowShareCreative}
                marking={telegramMarkingData}
                markableFile={activeCreative?.markableFile}
            />
        ),
        link: <LinkCell link={link} />,
        budget: (
            <BudgetCell
                budget={budget}
                onChange={onChangeBudget}
                edit={editBudget}
                onChangeEdit={onChangeEditBudget}
                enabled={!publishOptionFlag}
                disabledMessage="Чтобы изменить бюджет, уберите галочку 'Размещение забронировано' в форме маркировки"
                errorShakeKey={budgetErrorKey}
                hasMarkingFormData={hasMarkingFormData}
            />
        ),
        subscribers: (
            <SubscribersCell linkId={link_id} subscribers={subscribers} />
        ),
        averageReach: <AverageReachCell>{averageReach}</AverageReachCell>,
        err: <ErrCell>{err}</ErrCell>,
        CPMForcasted: <CpmCell>{CPMForcasted}</CpmCell>,

        publishedAt: <DateWithTimeCell>{publishedAt}</DateWithTimeCell>,
        publishingDeletionDate: (
            <DateWithTimeCell>{publishingDeletionDate}</DateWithTimeCell>
        ),
        reach: <ReachCell>{reach}</ReachCell>,
        CPM: <CpmCell>{CPM}</CpmCell>,
        subscribed: <>{subscribed}</>,
        subscribedPerDay: <>{subscribedPerDay}</>,
        CTR: <>{CTR.toFixed(2)}%</>,
        CPS: (
            <>
                {CPS.toFixed(0)} <RubleIconInline />
            </>
        ),
        unsubscribed: <>{unsubscribed}</>,
        unsubscriptionPercentage: (
            <div
                className={
                    unsubscriptionPercentage > 0
                        ? 'positive-percentage'
                        : unsubscriptionPercentage < 0
                          ? 'negative-percentage'
                          : ''
                }
            >
                {unsubscriptionPercentage.toFixed(2)}%
            </div>
        ),
        totalSubscribed: <>{totalSubscribed}</>,
        total_CPS: (
            <>
                {total_CPS.toFixed(0)} <RubleIconInline />
            </>
        ),
    }

    return (
        <tr ref={ref} id={trElementId}>
            {TABLE_COLUMNS_LAYOUT.map(({ key, style, className }) => (
                <td key={key} style={style} className={className}>
                    {tableRow[key]}
                </td>
            ))}
        </tr>
    )
})
